import { PaymentFormValues } from '@src/types/packages';
import { FC, useMemo } from 'react';
import { CheckoutDialog } from '@src/features/user/checkout/CheckoutDialog';
import { PurchaseResetPackage } from '@src/types';
import { BillingRenewAddon } from '@src/types/billing';

interface BillingRenewProps {
  open: boolean;
  renewPackage: PurchaseResetPackage | null;
  addons?: BillingRenewAddon[];
  loading?: boolean;
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: PaymentFormValues) => Promise<void> | void;
}

export const BillingRenew: FC<BillingRenewProps> = ({
  open,
  renewPackage,
  addons,
  loading,
  submitting,
  onOpenChange,
  onSubmit,
}) => {
  const isAddonMonthlySubscription = useMemo(() => {
    return !!addons?.some((a) => a.payment_handling_type === 'monthly');
  }, [addons]);

  const addonGroups = useMemo(() => {
    if (!addons?.length) return [];

    // Backend renews returns a flat list of addons. We show them in a single group.
    // If later backend returns addon categories, we can split into multiple groups.
    return [
      {
        title: 'Add Ons',
        addons: addons.map((a) => ({
          title: a.product?.display_name_web ?? a.product?.title ?? 'Addon',
          price: a.billing_plan?.first_price ?? a.product?.price ?? 0,
          monthlyPrice:
            a.payment_handling_type === 'monthly'
              ? (a.billing_plan?.addon_monthly_price ??
                a.billing_plan?.first_price ??
                a.product?.price ??
                0)
              : undefined,
        })),
      },
    ];
  }, [addons]);

  const productDisplayName = useMemo(() => {
    return (
      renewPackage?.display_name_web ??
      renewPackage?.display_name_app ??
      renewPackage?.title ??
      'Billing Renew'
    );
  }, [renewPackage]);

  const productMonthlyPrice = useMemo(() => {
    return renewPackage?.billing_plan?.second_price ?? 0;
  }, [renewPackage]);

  return (
    <CheckoutDialog
      addonGroups={addonGroups}
      addonMonthlyUnitPrice={5}
      isAddonMonthlySubscription={isAddonMonthlySubscription}
      loading={loading}
      open={open}
      package={renewPackage}
      packageDisplayName={productDisplayName}
      productMonthlyPrice={productMonthlyPrice}
      showMonthlyBreakdown={isAddonMonthlySubscription}
      submitting={submitting}
      title="Billing Renew"
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
    />
  );
};
